import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DatabaseIcon,
  RobotIcon,
  ShareNetworkIcon,
  SparkleIcon,
  TableIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import { Switch } from '~/components/ui/switch';
import { Segmented } from '~/components/ui/segmented';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { useConfirm } from '~/components/confirm-dialog';
import { api } from '~/lib/api-client';
import { InspectorSection } from '~/features/apps/inspector-section';
import { ApiTab } from '~/features/apps/edit-app';
import { resolveApplyScopesAction } from '~/features/apps/app-token-scopes';
import { ToolPickerDialog } from '~/features/apps/tool-picker-dialog';
import { toolAccessKeys, type ToolAccessResponse } from '~/features/apps/tool-access-keys';
import { AgentPickerDialog } from '~/features/apps/agent-picker-dialog';
import { agentAccessKeys, type AgentAccessResponse } from '~/features/apps/agent-access-keys';
import { cn } from '~/lib/utils';
import { useFeatureFlag, APPS_ASK_FLAG } from '~/hooks/queries/use-feature-flags';
import { WorkspaceRuleNote, workspaceRuleState } from './workspace-rule-note';
import { AppStorageBar } from './app-storage';
import {
  APP_POLICY_PATH,
  allowlistSummary,
  appPolicyKeys,
  ruleAllows,
  type AppPolicyResponse,
  type AppRuleKey,
} from './app-policy-model';

export type AgentRunAs = 'owner' | 'user';
export type AgentAskEngine = 'bounded' | 'orchestrator';

/**
 * Normalise the app's stored run-as mode for display (FRONT-1334).
 *
 * Exported and tested directly because the fallback is the load-bearing part:
 * every app published before this setting existed has no value, and the server
 * defaults those to `owner`. If this returned undefined for them the card
 * would render blank and a click could send the wrong mode back. Anything that
 * is not exactly `user` is `owner` — the permissive reading belongs on the
 * safer of the two modes.
 */
export function normalizeAgentRunAs(value: unknown): AgentRunAs {
  return value === 'user' ? 'user' : 'owner';
}

/**
 * Same fallback rule as `normalizeAgentRunAs`: anything that is not
 * exactly `orchestrator` is `bounded`. The safer reading belongs on the
 * engine that cannot write.
 */
export function normalizeAgentAskEngine(value: unknown): AgentAskEngine {
  return value === 'orchestrator' ? 'orchestrator' : 'bounded';
}

interface AppSettings {
  agentRunAs?: AgentRunAs;
  agentAskEngine?: AgentAskEngine;
  /** FRONT-2221: the approval card offers visitors "Always allow in this app". */
  allowRememberedActions?: boolean;
}

interface AppTokenInfo {
  token: string;
  scopes: string[];
  createdAt: string;
}

/**
 * The "Eddy tools" card's status text (FRONT-1385, reworked after product
 * testing). The other four cards' status describes WHAT is granted (Read,
 * Read/Write, As you, …); this one now just mirrors the switch itself —
 * `ON` in green once the owner has turned it on, `Off` otherwise — because
 * the picker moved out of the card and into a dialog (`ToolPickerDialog`),
 * so there's no longer room here to spell out a count. Pure and exported
 * for direct testing, same as `shouldMintForScopes` (`app-token-scopes.ts`).
 */
export function toolsToggleStatusLabel(enabled: boolean): string {
  return enabled ? 'ON' : 'Off';
}

/**
 * The "Eddy tools" card's secondary line — what used to be the whole status
 * label before the picker moved into a dialog. This is what keeps "ON" from
 * being misread as "something is callable": ON with nothing selected still
 * calls nothing, so this line says so plainly rather than silently reading
 * "0 tools". `N unavailable` mirrors a stale entry (D6, `src/apps/tool-offer.ts`
 * — reported to the owner, never silently pruned) the same way the old
 * combined label did. Pure and exported for direct testing.
 */
export function toolsSecondaryLine(selectedCount: number, staleCount: number): string {
  if (selectedCount === 0) return 'No tools selected yet';
  const toolWord = selectedCount === 1 ? 'tool' : 'tools';
  const base = `${selectedCount} ${toolWord} selected`;
  if (staleCount === 0) return base;
  return `${base} · ${staleCount} unavailable`;
}

/**
 * Whether turning the "Eddy tools" card off needs an explicit confirmation
 * (FRONT-1385 review finding 4). Unlike the other four cards, this switch's
 * off PUTs an empty allowlist and permanently destroys the owner's whole
 * selection — a single misclick on a card that could hold 20+ ticked tools
 * is unrecoverable, unlike the other switches' trivially-reversible off.
 * Only turning OFF a NON-EMPTY selection is destructive enough to warrant
 * asking; turning on, and turning off an already-empty selection, need no
 * confirmation. Pure and exported for direct testing, same as
 * `toolsSecondaryLine` above.
 */
export function needsToolsOffConfirm(nextEnabled: boolean, selectedCount: number): boolean {
  return !nextEnabled && selectedCount > 0;
}

/**
 * The "Eddy agents" card's status text — same shape as `toolsToggleStatusLabel`
 * above (FRONT-1714): the picker lives in its own dialog (`AgentPickerDialog`),
 * so the card just mirrors the switch — `ON` in success color once the owner
 * has allowlisted an agent, `Off` otherwise.
 */
export function agentsToggleStatusLabel(enabled: boolean): string {
  return enabled ? 'ON' : 'Off';
}

/**
 * The "Eddy agents" card's secondary line — same shape and reasoning as
 * `toolsSecondaryLine` above: `ON` with nothing selected still runs
 * nothing, so this says so plainly rather than reading "0 agents". The
 * zero case is written as a sentence ("No agents selected yet"), never a
 * bare "0 agents selected" — zero is the value this reads FOREVER once
 * nothing is ticked, and a bare statistic there reads as broken. Pure and
 * exported for direct testing.
 */
export function agentsSecondaryLine(selectedCount: number, staleCount: number): string {
  if (selectedCount === 0) return 'No agents selected yet';
  const agentWord = selectedCount === 1 ? 'agent' : 'agents';
  const base = `${selectedCount} ${agentWord} selected`;
  if (staleCount === 0) return base;
  return `${base} · ${staleCount} unavailable`;
}

/**
 * Whether turning the "Eddy agents" card off needs an explicit confirmation
 * — same shape and same reason as `needsToolsOffConfirm` above: this
 * switch's off PUTs an empty allowlist and permanently destroys the
 * owner's whole selection, unlike the other cards' trivially-reversible
 * off. Only turning OFF a NON-EMPTY selection warrants asking. Pure and
 * exported for direct testing.
 */
export function needsAgentsOffConfirm(nextEnabled: boolean, selectedCount: number): boolean {
  return !nextEnabled && selectedCount > 0;
}

/**
 * Capability cards for the app editor's Capabilities inspector section
 * (FRONT-912). Reads/writes the same app-token scopes as `ApiTab`'s
 * `ScopeEditor` — this is just a friendlier on/off front door for the three
 * capabilities apps actually use: KV store, knowledge graph, data store, Ask Eddy.
 * The raw scope/token/graph-allowlist controls stay reachable underneath
 * the "Advanced" disclosure (FRONT-2501, formerly "Manage token & graph
 * access" — it also holds the self-test, audit feed and SDK guide), which
 * renders `ApiTab` unchanged.
 */
export function CapabilitiesSection({ slug, showStorage = false }: { slug: string; showStorage?: boolean }) {
  // FRONT-2217: fallback true — the server refuses the grant and the ask
  // itself regardless, so a flags hiccup must not hide the card.
  const askFlagOn = useFeatureFlag(APPS_ASK_FLAG, true);
  const qc = useQueryClient();
  const confirm = useConfirm();

  // FRONT-2410: the workspace's Apps policy, shown under each agentic card.
  const { data: policyData } = useQuery({
    queryKey: appPolicyKeys.all,
    queryFn: () => api.get<AppPolicyResponse>(APP_POLICY_PATH),
  });
  // Loaded, well-formed data only — a missing policy shows no rule rather than a wrong one.
  const policy = policyData?.policy && typeof policyData.policy.agentic === 'boolean' ? policyData.policy : undefined;
  const ruleNote = (key: AppRuleKey, enabled: boolean, detail?: string) =>
    policy ? <WorkspaceRuleNote state={workspaceRuleState(policy, key, enabled)} detail={detail} /> : null;
  const ruleLocked = (key: AppRuleKey) => !!policy && !ruleAllows(policy, key);
  const visitorRunAsLocked = ruleLocked('visitorRunAs');

  const { data: tokenData, isLoading } = useQuery({
    queryKey: ['admin-app-token', slug],
    queryFn: () => api.get<{ token: AppTokenInfo | null }>(`/api/admin/apps/${slug}/token`),
  });
  const token = tokenData?.token ?? null;
  const scopes = token?.scopes ?? [];

  const mint = useMutation({
    mutationFn: (opts: { rotate: boolean; scopes?: string[] }) =>
      api.post<{ token: AppTokenInfo }>(`/api/admin/apps/${slug}/token`, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('API token ready');
    },
    onError: (e: Error) => toast.error(`Mint failed: ${e.message}`),
  });

  const saveScopes = useMutation({
    mutationFn: (next: string[]) =>
      api.patch<{ token: AppTokenInfo }>(`/api/admin/apps/${slug}/token/scopes`, { scopes: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('Scopes saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const { data: appData } = useQuery({
    queryKey: ['admin-app-settings', slug],
    queryFn: () => api.get<{ app: AppSettings }>(`/api/admin/apps/${slug}`),
  });
  const agentRunAs = normalizeAgentRunAs(appData?.app?.agentRunAs);
  const agentAskEngine = normalizeAgentAskEngine(appData?.app?.agentAskEngine);

  const saveRunAs = useMutation({
    mutationFn: (next: AgentRunAs) =>
      api.patch(`/api/admin/apps/${slug}`, { agentRunAs: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-settings', slug] });
      toast.success('Saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const saveRememberedActions = useMutation({
    mutationFn: (next: boolean) => api.patch(`/api/admin/apps/${slug}`, { allowRememberedActions: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-settings', slug] });
      toast.success('Saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const saveAskEngine = useMutation({
    mutationFn: (next: AgentAskEngine) =>
      api.patch(`/api/admin/apps/${slug}`, { agentAskEngine: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-settings', slug] });
      toast.success('Saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/api/admin/apps/${slug}/token`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success('API token revoked');
    },
    onError: (e: Error) => toast.error(`Revoke failed: ${e.message}`),
  });

  // FRONT-1385: the derived-scope counterpart of the four cards above. Shares
  // its cache entry with `ToolPickerDialog`'s own query (there's only one
  // list per app — no search-scoped variant, see tool-access-keys.ts) so
  // opening the "Select Tools" dialog below doesn't refetch.
  const { data: toolAccessData, isLoading: toolAccessLoading } = useQuery({
    queryKey: toolAccessKeys.list(slug),
    queryFn: () => api.get<ToolAccessResponse>(`/api/admin/apps/${slug}/tool-access`),
  });
  const toolsSelected = toolAccessData?.selected ?? [];
  const toolsStale = toolAccessData?.stale ?? [];
  const toolsGranted = toolsSelected.length > 0;

  // The Switch's own on/off doesn't map onto a single scope toggle the way
  // the other four cards' does — the grant is DERIVED from the allowlist
  // (D4: one switch, never two), so there's nothing to turn "on" until the
  // owner has actually ticked a tool inside the picker and saved. Turning the
  // card on therefore just flips this local flag on (immediately showing
  // "ON" in green and revealing the "Select Tools" button); turning it off
  // is the one direct action available here, and it PUTs an empty list
  // (never a silent local-only collapse — the revoke has to actually happen
  // server-side).
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const toolsEnabled = toolsGranted || toolsExpanded;
  const [toolsPickerOpen, setToolsPickerOpen] = useState(false);

  const revokeTools = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/apps/${slug}/tool-access`, { toolNames: [] as string[] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toolAccessKeys.lists(slug) });
      toast.success('EDDY tools turned off');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  async function setToolsEnabled(next: boolean) {
    if (next) {
      setToolsExpanded(true);
      return;
    }
    // FRONT-1385 review finding 4: this PUTs an empty allowlist and
    // permanently destroys the owner's whole selection — unlike the other
    // four cards' trivially-reversible off. One misclick on a 20-tool list
    // is unrecoverable, so require an explicit confirmation whenever
    // there's actually something to lose.
    if (needsToolsOffConfirm(next, toolsSelected.length)) {
      const ok = await confirm({
        title: 'Turn off EDDY tools?',
        description: `This clears all ${toolsSelected.length} ticked tool${toolsSelected.length === 1 ? '' : 's'} for this app. You'll need to re-tick them to turn it back on.`,
        confirmText: 'Turn off',
        destructive: true,
      });
      if (!ok) return;
    }
    setToolsExpanded(false);
    setToolsPickerOpen(false);
    revokeTools.mutate();
  }

  // FRONT-1714: the agent counterpart of the "Eddy tools" block above.
  // Shares its cache entry with `AgentPickerDialog`'s own query (one list
  // per app, no search-scoped variant — see agent-access-keys.ts) so
  // opening the "Select Agents" dialog below doesn't refetch.
  const { data: agentAccessData, isLoading: agentAccessLoading } = useQuery({
    queryKey: agentAccessKeys.list(slug),
    queryFn: () => api.get<AgentAccessResponse>(`/api/admin/apps/${slug}/agent-access`),
  });
  const agentsSelected = agentAccessData?.selected ?? [];
  // Never treat `stale` as meaningful while the catalogue fetch itself
  // degraded — see `AgentAccessResponse.catalogueDegraded`'s doc comment;
  // the card's secondary line must not report a stale count derived from
  // an empty degraded-fetch response.
  const agentsStale = agentAccessData?.catalogueDegraded ? [] : (agentAccessData?.stale ?? []);
  const agentsGranted = agentsSelected.length > 0;

  // Same "one switch, never two" shape as the tools block above (D4): the
  // grant is DERIVED from the allowlist, so there's nothing to turn "on"
  // until the owner has actually ticked an agent inside the picker and
  // saved.
  const [agentsExpanded, setAgentsExpanded] = useState(false);
  const agentsEnabled = agentsGranted || agentsExpanded;
  const [agentsPickerOpen, setAgentsPickerOpen] = useState(false);

  const revokeAgents = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/apps/${slug}/agent-access`, { agentIds: [] as string[] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentAccessKeys.lists(slug) });
      toast.success('EDDY agents turned off');
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  async function setAgentsEnabled(next: boolean) {
    if (next) {
      setAgentsExpanded(true);
      return;
    }
    // Same FRONT-1385-shaped guard as the tools card's off switch: this
    // PUTs an empty allowlist and permanently destroys the owner's whole
    // selection, so require an explicit confirmation whenever there's
    // actually something to lose.
    if (needsAgentsOffConfirm(next, agentsSelected.length)) {
      const ok = await confirm({
        title: 'Turn off EDDY agents?',
        description: `This clears all ${agentsSelected.length} allowlisted agent${agentsSelected.length === 1 ? '' : 's'} for this app. You'll need to re-tick them to turn it back on.`,
        confirmText: 'Turn off',
        destructive: true,
      });
      if (!ok) return;
    }
    setAgentsExpanded(false);
    setAgentsPickerOpen(false);
    revokeAgents.mutate();
  }

  const pending = mint.isPending || saveScopes.isPending || revoke.isPending;
  const runAsPending = saveRunAs.isPending;
  const askEnginePending = saveAskEngine.isPending;

  /**
   * Apply a new scope set. If there's no token yet, mint one seeded with
   * the requested scopes (first-enable flow); if the last remaining
   * capability was just toggled off, revoke the token instead of PATCHing
   * an empty scope set (the scopes route rejects that with a 400 — no
   * capabilities means no token needed); otherwise save scopes on the
   * existing token.
   *
   * Guard: skip minting when the computed next scope set is empty and no
   * token exists yet. The mint route (`src/admin/apps-routes.ts`) treats an
   * empty `scopes` array as "caller didn't specify" and silently defaults to
   * the broad `DEFAULT_APP_TOKEN_SCOPES` default (`app:kv:read` + `app:graph:read`)
   * — which would defeat every capability card's off-by-default guarantee.
   * There's nothing to mint for; no-op instead.
   */
  function applyScopes(next: string[]) {
    const action = resolveApplyScopesAction(!!token, next);
    switch (action.kind) {
      case 'noop':
        return;
      case 'mint':
        mint.mutate({ rotate: false, scopes: action.scopes });
        return;
      case 'revoke':
        revoke.mutate();
        return;
      case 'save':
        saveScopes.mutate(action.scopes);
        return;
    }
  }

  function withoutPrefix(list: string[], prefix: string): string[] {
    return list.filter((s) => !s.startsWith(prefix));
  }

  function setKvEnabled(enabled: boolean) {
    const base = withoutPrefix(scopes, 'app:kv:');
    applyScopes(enabled ? [...base, 'app:kv:read'] : base);
  }

  function setGraphEnabled(enabled: boolean) {
    const base = scopes.filter((s) => s !== 'app:graph:read');
    applyScopes(enabled ? [...base, 'app:graph:read'] : base);
  }

  function setDataEnabled(enabled: boolean) {
    const base = withoutPrefix(scopes, 'app:data:');
    applyScopes(enabled ? [...base, 'app:data:read'] : base);
  }

  function setDataWrite(write: boolean) {
    const base = withoutPrefix(scopes, 'app:data:');
    // Write implies read — keep/set read whenever write is on.
    const next = write ? [...base, 'app:data:read', 'app:data:write'] : [...base, 'app:data:read'];
    applyScopes(next);
  }

  function setAgentEnabled(enabled: boolean) {
    const base = scopes.filter((sc) => sc !== 'app:agents:ask');
    applyScopes(enabled ? [...base, 'app:agents:ask'] : base);
  }

  const agentEnabled = scopes.includes('app:agents:ask');
  const kvEnabled = scopes.includes('app:kv:read');
  const kvWrite = scopes.includes('app:kv:write');
  const graphEnabled = scopes.includes('app:graph:read');
  const dataEnabled = scopes.includes('app:data:read');
  const dataWrite = scopes.includes('app:data:write');

  return (
    <div className="space-y-3">
      <CapabilityCard
        icon={<DatabaseIcon size={18} />}
        name="Key–value store"
        description="Persistent key → JSON storage for this app (window.eddy.kv)."
        enabled={kvEnabled}
        statusLabel={kvEnabled ? (kvWrite ? 'Read/Write' : 'Read') : 'Off'}
        pending={pending || isLoading}
        onToggle={setKvEnabled}
      >
        {kvEnabled && (
          <ScopeChipRow
            readOn
            writeOn={kvWrite}
            onWriteChange={(v) => {
              const base = withoutPrefix(scopes, 'app:kv:');
              applyScopes(v ? [...base, 'app:kv:read', 'app:kv:write'] : [...base, 'app:kv:read']);
            }}
            disabled={pending}
          />
        )}
      </CapabilityCard>

      <CapabilityCard
        icon={<ShareNetworkIcon size={18} />}
        name="Knowledge graph"
        description="Read the allowlisted graph entities (window.eddy.graph)."
        enabled={graphEnabled}
        statusLabel={graphEnabled ? 'Read' : 'Off'}
        pending={pending || isLoading}
        onToggle={setGraphEnabled}
      />

      <CapabilityCard
        icon={<TableIcon size={18} />}
        name="Data store"
        description="Store & query this app's own records (window.eddy.data)."
        badge="New"
        enabled={dataEnabled}
        statusLabel={dataEnabled ? (dataWrite ? 'Read/Write' : 'Read') : 'Off'}
        pending={pending || isLoading}
        onToggle={setDataEnabled}
      >
        {dataEnabled && (
          <ScopeChipRow
            readOn
            writeOn={dataWrite}
            onWriteChange={setDataWrite}
            disabled={pending}
          />
        )}
        {showStorage && (
          <AppStorageBar slug={slug} inUse={dataEnabled} className="mt-3 border-t border-border pt-3" />
        )}
      </CapabilityCard>

      {askFlagOn && (
      <CapabilityCard
        icon={<SparkleIcon size={18} />}
        name="Ask EDDY"
        description="Lets the app call window.eddy.ask (or a backend handler). Off by default. 'As you' is a lookup against your EDDY. 'As the visitor' uses theirs (signed-in only) — optionally their full EDDY."
        badge="New"
        enabled={agentEnabled}
        statusLabel={
          agentEnabled
            ? agentRunAs === 'user'
              ? agentAskEngine === 'orchestrator'
                ? 'As the visitor · full EDDY'
                : 'As the visitor'
              : 'As you'
            : 'Off'
        }
        pending={pending || isLoading}
        onToggle={setAgentEnabled}
        workspace={ruleNote('ask', agentEnabled)}
        locked={ruleLocked('ask')}
      >
        {agentEnabled && (
          <div className="space-y-2">
            <Segmented<AgentRunAs>
              size="sm"
              aria-label="Who EDDY answers as"
              value={agentRunAs}
              onChange={(next) => {
                if (next !== agentRunAs && !runAsPending) saveRunAs.mutate(next);
              }}
              options={[
                { value: 'owner', label: 'As you' },
                // FRONT-2410: can be left, never newly chosen, while the workspace blocks it.
                { value: 'user', label: 'As the visitor', disabled: visitorRunAsLocked && agentRunAs !== 'user' },
              ]}
            />
            <p className="text-xs text-muted-foreground">
              {agentRunAs === 'owner'
                ? toolsGranted
                  ? 'Answers come from your data, and work for everyone — including visitors who are not signed in. Asking a question is lookup-only and never writes — but EDDY tools (below) is separately turned on for this app and can write as you.'
                  : 'Answers come from your data, and work for everyone — including visitors who are not signed in. Lookup only — asking a question never writes as you.'
                : 'Answers come from each visitor’s own data and count against their usage. Visitors who are not signed in get a clear refusal instead of your data.'}
            </p>
            {visitorRunAsLocked && ruleNote('visitorRunAs', agentRunAs === 'user')}
            {agentRunAs === 'user' && (
              <div className="flex items-start justify-between gap-3 pt-1">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground">
                    Use the visitor{"’"}s full EDDY
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Their stores, their budget, their tools — send email, write notes, run workflows.
                    Signed-in only. {"‘"}As you{"’"} stays a lookup — asking a question never
                    writes with your token (EDDY tools, if turned on for this app, is a separate
                    write path).
                  </p>
                </div>
                <Switch
                  checked={agentAskEngine === 'orchestrator'}
                  onCheckedChange={(on) => {
                    const next: AgentAskEngine = on ? 'orchestrator' : 'bounded';
                    if (next !== agentAskEngine && !askEnginePending) saveAskEngine.mutate(next);
                  }}
                  disabled={askEnginePending}
                  aria-label="Use the visitor's full EDDY"
                  className="mt-0.5 shrink-0"
                />
              </div>
            )}
          </div>
        )}
      </CapabilityCard>
      )}

      <CapabilityCard
        icon={<WrenchIcon size={18} />}
        name="EDDY tools"
        description="Lets the app call specific EDDY tools directly — Projects, Files, the knowledge graph, web search, image generation and more (window.eddy.tools). Off by default. Anyone who opens the published app can run a ticked tool — as you, on your compute budget — so pick only what the app genuinely needs."
        enabled={toolsEnabled}
        statusLabel={toolsToggleStatusLabel(toolsEnabled)}
        pending={revokeTools.isPending || toolAccessLoading}
        onToggle={setToolsEnabled}
        workspace={ruleNote(
          'tools',
          toolsEnabled,
          policy && policyData?.tools && ruleAllows(policy, 'tools') && policy.toolAllowlist !== null ? allowlistSummary(policy.toolAllowlist, policyData.tools.length, 'tools') : undefined,
        )}
        locked={ruleLocked('tools')}
      >
        {toolsEnabled && (
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {toolsSecondaryLine(toolsSelected.length, toolsStale.length)}
            </p>
            <Button size="sm" variant="outline" onClick={() => setToolsPickerOpen(true)}>
              Select Tools
            </Button>
          </div>
        )}
      </CapabilityCard>
      <ToolPickerDialog slug={slug} open={toolsPickerOpen} onOpenChange={setToolsPickerOpen} />

      <CapabilityCard
        icon={<RobotIcon size={18} />}
        name="EDDY agents"
        description="Lets the app run specific EDDY agents — each one using its own tools (window.eddy.agents). Off by default. Only people signed in to EDDY in this workspace can run one, and a run can take minutes. 'As you' uses your connections; 'As the visitor' uses theirs."
        enabled={agentsEnabled}
        statusLabel={agentsToggleStatusLabel(agentsEnabled)}
        pending={revokeAgents.isPending || agentAccessLoading}
        onToggle={setAgentsEnabled}
        workspace={ruleNote(
          'agents',
          agentsEnabled,
          policy && policyData?.agents && ruleAllows(policy, 'agents') && policy.agentAllowlist !== null ? allowlistSummary(policy.agentAllowlist, policyData.agents.length, 'agents') : undefined,
        )}
        locked={ruleLocked('agents')}
      >
        {agentsEnabled && (
          <div className="mt-1.5 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {agentsSecondaryLine(agentsSelected.length, agentsStale.length)}
              </p>
              <Button size="sm" variant="outline" onClick={() => setAgentsPickerOpen(true)}>
                Select Agents
              </Button>
            </div>
            {/* FRONT-2218: the SAME per-app setting as Ask EDDY's run-as (one
                column, `agentRunAs`) — shown here too because it now decides
                whose connections an agent run uses. */}
            <Segmented<AgentRunAs>
              size="sm"
              aria-label="Who agents run as"
              value={agentRunAs}
              onChange={(next) => {
                if (next !== agentRunAs && !runAsPending) saveRunAs.mutate(next);
              }}
              options={[
                { value: 'owner', label: 'As you' },
                // FRONT-2410: can be left, never newly chosen, while the workspace blocks it.
                { value: 'user', label: 'As the visitor', disabled: visitorRunAsLocked && agentRunAs !== 'user' },
              ]}
            />
            <p className="text-xs text-muted-foreground">
              {agentRunAs === 'owner'
                ? 'Agents run with your connections and count against your usage. Visitors must be signed in, and they act through your accounts.'
                : 'Agents run with each signed-in visitor’s own connections (their mailbox, their calendar) and count against their usage. Never falls back to your accounts.'}
              {' '}This is the same setting as Ask EDDY’s “As you / As the visitor” choice above — changing it here changes that too.
            </p>
            {visitorRunAsLocked && ruleNote('visitorRunAs', agentRunAs === 'user')}
            {/* FRONT-2221: actions need the visitor's OK on a card; the owner
                may let visitors remember a yes for this app. */}
            {agentRunAs === 'user' && (
              <div className="pt-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground">Let visitors say “Always allow”</p>
                    <p className="text-xs text-muted-foreground">
                      When an agent wants to send, create or post, the visitor approves it on a card. With this on, the
                      card also offers “Always allow in this app” — their own choice, for their own account, and they can
                      take it back in their EDDY settings.
                    </p>
                  </div>
                  <Switch
                    checked={!!appData?.app?.allowRememberedActions}
                    onCheckedChange={(on) => !saveRememberedActions.isPending && saveRememberedActions.mutate(on)}
                    disabled={saveRememberedActions.isPending || ruleLocked('agentActions')}
                    aria-label="Let visitors say Always allow"
                    className="mt-0.5 shrink-0"
                  />
                </div>
                {ruleLocked('agentActions') && ruleNote('agentActions', false)}
              </div>
            )}
          </div>
        )}
      </CapabilityCard>
      <AgentPickerDialog slug={slug} open={agentsPickerOpen} onOpenChange={setAgentsPickerOpen} />

      <InspectorSection
        title="Advanced"
        defaultOpen={false}
        className="border border-border rounded-md"
      >
        <ApiTab slug={slug} />
      </InspectorSection>
    </div>
  );
}

/** FRONT-2473: also the Building Session's "While building" rows, so both sections look alike. */
export function CapabilityCard({
  icon,
  name,
  description,
  badge,
  enabled,
  statusLabel,
  pending,
  onToggle,
  children,
  workspace,
  locked,
}: {
  icon: ReactNode;
  name: string;
  description: string;
  badge?: string;
  enabled: boolean;
  statusLabel: string;
  pending: boolean;
  onToggle: (next: boolean) => void;
  children?: ReactNode;
  /** FRONT-2410: the workspace's rule for this card, under its description. */
  workspace?: ReactNode;
  /** FRONT-2410: the workspace blocks it — it can be switched off here, never on. */
  locked?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-card/40 p-3">
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className={cn(
            'mt-0.5 shrink-0 text-muted-foreground',
            enabled && 'text-success',
          )}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{name}</span>
            <span
              className={cn(
                'text-[length:var(--text-2xs)] font-medium',
                enabled ? 'text-success' : 'text-muted-foreground',
              )}
            >
              {statusLabel}
            </span>
            {badge && <Badge variant="secondary">{badge}</Badge>}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          {workspace}
          {children}
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          disabled={pending || (!!locked && !enabled)}
          aria-label={`Toggle ${name}`}
          className="mt-0.5 shrink-0"
        />
      </div>
    </div>
  );
}

function ScopeChipRow({
  readOn,
  writeOn,
  onWriteChange,
  disabled,
}: {
  readOn: boolean;
  writeOn: boolean;
  onWriteChange: (next: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <Badge variant={readOn ? 'success-soft' : 'outline'}>Read</Badge>
      <button
        type="button"
        role="switch"
        aria-checked={writeOn}
        aria-label="Toggle write access"
        onClick={() => onWriteChange(!writeOn)}
        disabled={disabled}
        className={cn(
          'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          writeOn
            ? 'border-success/30 bg-success/15 text-success'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        Write
      </button>
    </div>
  );
}
